import { AppThunk, AppDispatch } from '@app/store/store'
import { apiSdk } from '@app/api/endpoints'
import { runtimeConfig } from '@app/common/config'
import { wait } from '@app/api/utils'
import { useCheckSetFieldValueResponse } from '@app/hooks/useCheckSetFieldValueResponse'
import catchErrorHandler from '@app/store/utils/catchErrorHandler'
import { API_FIELDS_DATA_KEYS, INSURANCE_PROVIDER_NPI_ID, INSURANCE_SERVICE_TYPE_CODE } from '@app/constants/fields'
import { Field } from '@app/api/types'

import { actions } from './slice'
import { StoreValueRequest } from './types'

const AVAILITY_CHECKER_DELAY = 900
const MAX_AVAILITY_DELAY = 12000

const CHANNEL_ID = runtimeConfig.joinSproutTherapyConfig?.CHANNEL_ID

const DEFAULT_INSURANCE_DATA = {
  remainingDeductible: '2000',
  coInsurance: '20',
  remainingOutOfPocket: '4000'
}

const storeValues = async (channelId: string, sessionId: string, values: StoreValueRequest[]) => {
  return await Promise.all(
    values.map(async value => {
      await apiSdk.SetFieldValue({
        channelId: channelId,
        sessionId: sessionId,
        field: value.key,
        value: value.value
      })
    })
  )
}

export const updateValue = (sessionId: string, field: Field): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(actions.requestUpdateCostCalculatorInputData())

  try {
    if (field.apiFieldKey) {
      const response = await apiSdk.SetFieldValue({
        channelId: CHANNEL_ID,
        sessionId: sessionId,
        field: field.apiFieldKey,
        value: field.fieldValue
      })

      useCheckSetFieldValueResponse(response)
    }

    dispatch(actions.successUpdateCostCalculatorInputData(field))
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorUpdateCostCalculatorInputData)
  }
}

const calculateActualOutput = () => {
  const result = []

  const youPayPerHourFrom = 45 * 0.3
  const youPayPerHourTo = 55 * 0.3

  result.push(
    {
      hoursPerWeek: 10,
      hoursPerWeekText: 'low severity',
      report: {
        youPayFrom: 10 * youPayPerHourFrom,
        youPayTo: 10 * youPayPerHourTo
      }
    },
    {
      hoursPerWeek: 20,
      hoursPerWeekText: 'moderate severity',
      report: {
        youPayFrom: 20 * youPayPerHourFrom,
        youPayTo: 20 * youPayPerHourTo
      }
    },
    {
      hoursPerWeek: 30,
      hoursPerWeekText: 'high severity',
      report: {
        youPayFrom: 30 * youPayPerHourFrom,
        youPayTo: 30 * youPayPerHourTo
      }
    }
  )

  return result
}

export const initCostCalculator = (sessionId: string): AppThunk => async (dispatch: AppDispatch) => {
  const valuesToStore = []

  dispatch(actions.requestInitCostCalculator())

  try {
    valuesToStore.push({
      sessionId,
      key: API_FIELDS_DATA_KEYS.insuranceProviderNpiFieldKey,
      value: INSURANCE_PROVIDER_NPI_ID
    })
    valuesToStore.push({
      sessionId,
      key: API_FIELDS_DATA_KEYS.insuranceServiceTypeFieldKey,
      value: INSURANCE_SERVICE_TYPE_CODE
    })

    await storeValues(CHANNEL_ID, sessionId, valuesToStore)

    dispatch(actions.successInitCostCalculator())
    dispatch(updateCostCalculatorOutput())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorInitCostCalculator)
  }
}

export const initInsuranceCoverageReport = (sessionId: string): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(actions.requestInitInsuranceCoverageReport())

  try {
    const response = await apiSdk.InitInsuranceCoverageReport({ channelId: CHANNEL_ID, sessionId: sessionId })

    if (response.channelV2.__typename === 'ChannelError') {
      throw new Error(response.channelV2.code)
    }

    if (response.channelV2.sessionV2.__typename === 'SessionError') {
      throw new Error(response.channelV2.sessionV2.code)
    }

    if (response.channelV2.sessionV2.insuranceCoverageReportV2.__typename === 'InsuranceCoverageReportError') {
      throw new Error(response.channelV2.sessionV2.insuranceCoverageReportV2.code)
    }

    if (response.channelV2.sessionV2.insuranceCoverageReportV2.initV2.__typename === 'MissingFieldsError') {
      throw new Error(response.channelV2.sessionV2.insuranceCoverageReportV2.initV2.fieldsErrorCode)
    }

    if (response.channelV2.sessionV2.insuranceCoverageReportV2.initV2.__typename === 'ParentNameError') {
      throw new Error(response.channelV2.sessionV2.insuranceCoverageReportV2.initV2.parentNameErrorCode)
    }

    const fieldsIds = response.channelV2.sessionV2.insuranceCoverageReportV2.initV2.ids

    const reportId = fieldsIds && fieldsIds.length > 0 ? fieldsIds[0] : null

    if (reportId) {
      await wait(AVAILITY_CHECKER_DELAY)

      dispatch(actions.successInitInsuranceCoverageReport(reportId))

      dispatch(fetchInsuranceCostReport(sessionId, reportId))
    } else {
      dispatch(actions.errorInitInsuranceCoverageReport('Availity request failed'))
    }
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorInitInsuranceCoverageReport)
  }
}

export const fetchInsuranceCostReport = (sessionId: string, requestId: string, timestamp?: number): AppThunk => async (
  dispatch: AppDispatch
) => {
  dispatch(actions.requestFetchInsuranceCostReport())

  try {
    const result = await apiSdk.GetInsuranceCostReport({
      channelId: CHANNEL_ID,
      sessionId: sessionId,
      reportId: requestId
    })

    const channel = result.channelsV2?.length ? result.channelsV2[0] : null
    const session = channel?.sessionsV2?.length ? channel.sessionsV2[0] : null
    const availityResult = session?.insuranceCostsReport

    if (availityResult?.statusCode === '0') {
      // in progress
      const now = new Date().getTime()
      if (timestamp && now - timestamp > MAX_AVAILITY_DELAY) {
        // waiting too long
        dispatch(
          actions.successFetchInsuranceCostReport({
            requestId: availityResult?.requestId || requestId,
            status: availityResult?.status || 'UNKNOWN',
            statusCode: availityResult?.statusCode || '',
            isOutOfNetwork: availityResult?.isOutOfNetwork || true,
            coInsurance: availityResult?.coInsurance || '20',
            remainingOutOfPocket: availityResult?.remainingOutOfPocket || '4000',
            remainingDeductible: availityResult?.remainingDeductible || '2000'
          })
        )
      } else {
        await wait(AVAILITY_CHECKER_DELAY)

        dispatch(fetchInsuranceCostReport(sessionId, requestId, timestamp || now))
      }
    } else {
      dispatch(
        actions.successFetchInsuranceCostReport({
          requestId: availityResult?.requestId || requestId,
          status: availityResult?.status || 'UNKNOWN',
          statusCode: availityResult?.statusCode || '',
          isOutOfNetwork: availityResult?.isOutOfNetwork || true,
          coInsurance: availityResult?.coInsurance || '20',
          remainingOutOfPocket: availityResult?.remainingOutOfPocket || '4000',
          remainingDeductible: availityResult?.remainingDeductible || '2000',
          ...(availityResult?.statusCode === '4' ? {} : DEFAULT_INSURANCE_DATA)
        })
      )
    }
  } catch (error) {
    dispatch(actions.errorFetchInsuranceCostReport(error.errors?.[0]?.message))
  }
}

export const updateCostCalculatorOutput = (): AppThunk => async (dispatch: AppDispatch) => {
  const outputData = calculateActualOutput()

  dispatch(actions.updateCostCalculatorOutput(outputData))
}
